import React, { useContext } from 'react';
import * as R from 'ramda';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Link as RouterLink } from 'react-router-dom';
import { useUserRolePermissions } from 'hooks';
import PropTypes from 'prop-types';
import CounterChip from '../components/CounterChip';
import { renderInput } from '../../utils/renderUtils';
import { useGetTerminalsQuery } from '../../customerAuthorizationsEndpoints';
import { CreateCustomerAuthContext, initialStateStep3, initialStateStep4 } from '../Create';

const resetState = R.mergeLeft(initialStateStep4, initialStateStep3);

export const renderTags = (tags, getTagProps) =>
  tags.map((item, index) => (
    <Chip
      label={item.terminalShortName}
      sx={{ borderColor: 'secondary.main', borderRadius: 25 }}
      variant="outlined"
      {...getTagProps({ index })}
    />
  ));

const Terminals = ({ isSelectedTerminalAuthorized }) => {
  const {
    info: { controlType, customerId: customerIdName, customerName, terminals },
    setInfo,
  } = useContext(CreateCustomerAuthContext);

  const { currentAffiliate = {} } = useUserRolePermissions();
  const { customerIdName: companyCustomerIdName, dataCompanyId } = currentAffiliate;

  const params = {
    customerIdName,
    supplier: { companyCustomerIdName, dataCompanyId },
  };

  const isSingleSelection = R.equals('SINGLE', controlType);
  const isMultipleSelection = R.not(isSingleSelection);
  const displayCounter = !isSingleSelection && terminals.length > 0;

  // prettier-ignore
  const getOptionLabel = R.compose(
    R.ifElse(
      R.isNil, 
      R.always(''), 
      R.prop('terminalShortName')
    ),
    R.when(R.is(Array), R.head)
  );

  const { data: terminalsQuery = [], isFetching: isFetchingTerminals } = useGetTerminalsQuery({
    params,
  });

  // prettier-ignore
  // handleChange :: _ -> Terminal -> [Terminal]
  const handleChange = R.compose(
    setInfo,
    R.curryN(2, R.compose(
      R.mergeLeft(resetState),
      R.assoc('products', []),
      R.assoc('terminals'))
    ),
    R.flatten,
    R.of,
    R.nthArg(1)
  );

  const selectedTerminal = R.head(terminals);

  return (
    <Card>
      <CardContent>
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <Typography variant="subtitle1">Terminals</Typography>
          {displayCounter > 0 && <CounterChip count={terminals.length} />}
        </Stack>
        <Autocomplete
          getOptionLabel={getOptionLabel}
          isOptionEqualToValue={R.eqProps('terminalId')}
          loading={isFetchingTerminals}
          multiple={isMultipleSelection}
          onChange={handleChange}
          options={terminalsQuery}
          renderInput={renderInput({
            label: 'Search Terminals',
            length: terminals.length,
            loading: isFetchingTerminals,
          })}
          renderTags={renderTags}
          value={terminals}
        />
      </CardContent>
      {isSelectedTerminalAuthorized && (
        <CardActions sx={{ px: 3 }}>
          <Typography>
            This terminal already has an authorization for customer {customerIdName} {customerName}.
            Select a different terminal or edit the existing record.
          </Typography>
          <Button
            component={RouterLink}
            sx={{ ml: 3 }}
            to={`../../edit/${selectedTerminal.customerAuthorizationId}`}
            variant="text"
          >
            Edit Existing Auth.
          </Button>
        </CardActions>
      )}
    </Card>
  );
};

Terminals.propTypes = {
  isSelectedTerminalAuthorized: PropTypes.bool.isRequired,
};

export default Terminals;
